import { useState } from 'react';
import Avatar from 'funifier-nice-avatar';

import type { RostoDoAutor, RostoDoDestinatario } from 'services/home-feed.service';

export interface PersonFaceSource {
  /** Endereço da foto (`player.image.small.url`). */
  image?: string | null;
  /** Avatar montado pela pessoa (`player.extra.avatar`). */
  avatar?: Record<string, any> | null;
  /** Escolha da pessoa (`player.extra.avatar_type`): 0 ou ausente = avatar montado, 1 = foto. */
  avatarType?: number | null;
}

/** O rosto do autor gravado no cache do feed (`authorImage`, `authorAvatar`, `authorAvatarType`) no formato do `PersonAvatar`. */
export function rostoDoAutor({ authorImage, authorAvatar, authorAvatarType }: RostoDoAutor): PersonFaceSource {
  return { image: authorImage, avatar: authorAvatar, avatarType: authorAvatarType };
}

/** O rosto de quem recebeu o reconhecimento (`recipientImage`, `recipientAvatar`, `recipientAvatarType`). */
export function rostoDoDestinatario({ recipientImage, recipientAvatar, recipientAvatarType }: RostoDoDestinatario): PersonFaceSource {
  return { image: recipientImage, avatar: recipientAvatar, avatarType: recipientAvatarType };
}

export type PersonFace =
  | { kind: 'avatar'; avatar: Record<string, any> }
  | { kind: 'photo'; url: string; onError: () => void }
  | { kind: 'initials' };

/**
 * Qual rosto mostrar. A regra é a do `UserAvatar` do cartão de perfil: `avatar_type` 0 (ou
 * ausente) mostra o avatar montado; 1 mostra a foto. Se a foto não carrega (`onError`), cai no
 * avatar e, sem ele, nas iniciais, em vez de deixar o círculo vazio.
 *
 * Cada tela desenha o rosto do seu jeito; a escolha entre avatar, foto e iniciais é só esta.
 */
export function usePersonFace({ image, avatar, avatarType }: PersonFaceSource): PersonFace {
  const [photoFailed, setPhotoFailed] = useState(false);

  const builtAvatar = avatar && Object.keys(avatar).length > 0 ? avatar : null;
  const photo = image && !photoFailed ? image : null;

  if (builtAvatar && (!avatarType || !photo)) return { kind: 'avatar', avatar: builtAvatar };
  if (photo) return { kind: 'photo', url: photo, onError: () => setPhotoFailed(true) };

  return { kind: 'initials' };
}

/** Iniciais das duas primeiras palavras do nome ("PRISCILA NOGUEIRA" → "PN"). */
export function initials(name?: string) {
  return (name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(word => word.charAt(0).toUpperCase())
    .join('');
}

interface PersonAvatarProps extends PersonFaceSource {
  /** O que aparece quando não há avatar nem foto. */
  initials: string;
  /** Lado do círculo, em px. */
  size?: number;
  /** Cores e tipo das iniciais: cada tela tem as suas. */
  initialsClassName?: string;
}

const FRAME_CLASSES = 'block shrink-0 overflow-hidden rounded-full';
const DEFAULT_INITIALS_CLASSES = 'bg-brand-purple-light font-display text-xs font-bold text-brand-blue-dark';

/**
 * Rosto de uma pessoa nas telas da nova identidade: o avatar montado, a foto, ou as iniciais,
 * pela regra do `usePersonFace`.
 *
 * É decorativo (`aria-hidden`, `alt=""`): quem usa escreve o nome da pessoa ao lado.
 */
export function PersonAvatar({
  image,
  avatar,
  avatarType,
  initials,
  size = 38,
  initialsClassName = DEFAULT_INITIALS_CLASSES,
}: PersonAvatarProps) {
  const face = usePersonFace({ image, avatar, avatarType });
  const frame = { width: size, height: size };

  if (face.kind === 'avatar') {
    return (
      <span aria-hidden="true" data-testid="person-avatar" style={frame} className={FRAME_CLASSES}>
        <Avatar style={frame} {...face.avatar} />
      </span>
    );
  }

  if (face.kind === 'photo') {
    return <img src={face.url} alt="" style={frame} className={`${FRAME_CLASSES} object-cover`} onError={face.onError} />;
  }

  return (
    <span
      aria-hidden="true"
      style={frame}
      className={`flex shrink-0 items-center justify-center rounded-full ${initialsClassName}`}
    >
      {initials}
    </span>
  );
}
